import React, { useState } from "react";
import { shareOrDownloadFile } from "./lib/shareFile";

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function buildExportCanvas({
  imageUrl,
  designName = "",
  artistName = "",
  offsetX = 0,
  offsetY = 0,
  zoom = 1.2,
  overlaySrc = "/TT_Square_Overlay.png",
}) {
  const [img, border] = await Promise.all([
    loadImage(imageUrl),
    loadImage(overlaySrc),
  ]);

  const size = 2160;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, size, size);

  const padding = 160;
  const imageAreaW = size - padding * 2;
  const imageAreaH = size - padding * 2;

  const baseScale = Math.min(imageAreaW / img.width, imageAreaH / img.height);
  const scale = baseScale * zoom;

  const w = img.width * scale;
  const h = img.height * scale;

  const x = (size - w) / 2 + offsetX;
  const y = (size - h) / 2 + offsetY;

  ctx.drawImage(img, x, y, w, h);

  ctx.drawImage(border, 0, 0, size, size);

  ctx.textBaseline = "top";
  ctx.shadowColor = "rgba(0,0,0,0.55)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 4;

  ctx.fillStyle = "#fff";
  ctx.font = "120px Staatliches, Impact, sans-serif";
  ctx.fillText((designName || "Tattoo").toUpperCase(), 120, size - 300);

  ctx.fillStyle = "rgba(255,255,255,0.88)";
  ctx.font = "48px Poppins, sans-serif";
  ctx.fillText(
    artistName ? `Flash by ${artistName}` : "Custom Design",
    120,
    size - 185
  );

  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "36px Poppins, sans-serif";
  ctx.fillText("tattune.ca", size - 250, size - 185);

  return canvas;
}

function canvasToBlob(canvas, type = "image/png", quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

export default function ExportPreviewButton({
  className = "tt-btn",
  onExportPreview,
  designName = "",
  artistName = "",
}) {
  const [busy, setBusy] = useState(false);

  async function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (busy) return;
    setBusy(true);

    try {
      let imageUrl = null;

      if (typeof onExportPreview === "function") {
        imageUrl = await onExportPreview({
          width: 2048,
          height: 2048,
          format: "image/png",
        });
      }

      if (!imageUrl) {
        console.warn("[export] no export image available");
        return;
      }

      const canvas = await buildExportCanvas({
        imageUrl,
        designName,
        artistName,
        overlaySrc: "/TT_Square_Overlay.png",
      });

      const blob = await canvasToBlob(canvas, "image/png");
      if (!blob) {
        console.warn("[export] failed to build image blob");
        return;
      }

      await shareOrDownloadFile({
        blob,
        filename: "tattune-preview.png",
        title: "Tattune Preview",
        text: designName || "Tattoo Preview",
      });
    } catch (err) {
      console.warn("[export] preview export failed", err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={busy}
    >
      {busy ? "Exporting…" : "Share Preview"}
    </button>
  );
}